import { InputHTMLAttributes, ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import tw from "twin.macro";

import { Colors } from "../../styles";
import { registerMock } from "../../utils";

const Base = styled.input`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${Colors.blue["01"]};
  position: relative;

  ${tw`checked:bg-gradient-theme-to-l cursor-pointer`}

  &:before {
    content: "";
    display: block;
    width: 8px;
    height: 2px;
    border-radius: 6px;
    background: ${Colors.blue["01"]};
    transform: rotate(40deg);
    position: absolute;
    top: 12px;
    left: 2px;
  }

  &:after {
    content: "";
    display: block;
    width: 14px;
    height: 2px;
    border-radius: 6px;
    background: ${Colors.blue["01"]};
    transform: rotate(-55deg);
    position: absolute;
    top: 9px;
    right: 1px;
  }
`;

const labelStyle = tw`font-base font-light text-xs leading-3 ml-2 cursor-pointer`;

const outlinedStyle = tw`bg-white border border-solid border-black`;

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  name: string;
  label: ReactNode;
  size?: "small" | "default";
  variant?: "default" | "outlined";
}

export const CheckBox = ({
  name,
  label,
  size = "default",
  variant = "default",
  disabled,
  checked,
  onChange,
  ...rest
}: Props) => {
  const form = useFormContext();
  const register = form ? form.register : registerMock;
  const isChecked = checked || (form && form.watch(name));

  const commonProps = {
    ...register(name),
    type: "checkbox",
    id: name,
    onChange,
    checked,
    ...rest,
  };
  const base = (
    <>
      <Base
        {...commonProps}
        disabled={disabled}
        css={[
          disabled && tw`cursor-not-allowed`,
          !isChecked && variant === "outlined" && outlinedStyle,
        ]}
      />
      <label
        css={[labelStyle, disabled && tw`text-secondary cursor-not-allowed`]}
        htmlFor={name}
      >
        {label}
      </label>
    </>
  );

  switch (size) {
    case "default": {
      return base;
    }
    case "small": {
      return (
        <>
          <Base
            {...commonProps}
            css={[
              {
                width: 15,
                height: 15,
                ":before": {
                  width: 6,
                  top: 9,
                },
                ":after": {
                  width: 10,
                  top: 7,
                },
              },
              disabled && tw`cursor-not-allowed`,
              !isChecked && variant === "outlined" && outlinedStyle,
            ]}
          />
          <label
            css={[
              labelStyle,
              tw`font-normal text-2xs`,
              disabled && tw`text-secondary cursor-not-allowed`,
            ]}
            htmlFor={name}
          >
            {label}
          </label>
        </>
      );
    }
    default: {
      return base;
    }
  }
};
